// global
import React, { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
// components
import { Popup } from 'semantic-ui-react';
// stylesheet
import styles from './DefaultPopup.module.scss';

function DefaultPopup(props) {
  const popupContainerRef = useRef(null);
  const buttonRef = useRef(null); // Ref para o botão de trigger do popup

  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const { children, tag } = props;

  // Função que calcula a posição do popup
  const calculatePopupPosition = () => {
    if (buttonRef.current && popupContainerRef.current) {
      const buttonDOMrect = buttonRef.current.getBoundingClientRect();
      let buttonRefElement = buttonRef.current;
      let offsetTop = 0;

      while (buttonRefElement) {
        offsetTop += buttonRefElement.offsetTop;
        buttonRefElement = buttonRefElement.offsetParent;
      }

      // Obtém o contêiner pai rolável
      const scrollableContainer = popupContainerRef.current.closest('#scrollable');

      let scrollTopOffset = 0;
      if (scrollableContainer) {
        // Verifica o scroll do contêiner
        scrollTopOffset = scrollableContainer.scrollTop;
      }

      // Levando em consideração o scroll do contêiner
      const POSITIONING_FACTOR = buttonDOMrect.height * 1.9;
      setPopupPosition({ top: offsetTop - POSITIONING_FACTOR - scrollTopOffset });
    }
  };

  const setupResizeObserver = useCallback(buttonNode => {
    // Usa o ResizeObserver para recalcular a posição quando o botão ou o contêiner for redimensionado
    const resizeObserver = new ResizeObserver(() => {
      calculatePopupPosition();
    });

    // Observa o botão para recalcular a posição sempre que o botão for redimensionado
    if (buttonNode) {
      resizeObserver.observe(buttonNode);
    }

    return resizeObserver;
  }, []);

  const setupMutationObserver = useCallback(containerNode => {
    // Utiliza o MutationObserver para observar o comportamento de scroll e outras mudanças no contêiner
    const mutationObserver = new MutationObserver(() => {
      calculatePopupPosition();
    });

    if (containerNode) {
      mutationObserver.observe(containerNode, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });
    }

    return mutationObserver;
  }, []);

  useEffect(() => {
    // Inicializa a posição do popup ao carregar o componente
    calculatePopupPosition();

    const containerNode = popupContainerRef.current;
    const buttonNode = buttonRef.current;

    // Inicializa o Resize Observer
    const resizeObserver = setupResizeObserver(buttonNode);

    // Inicializa o Mutation Observer
    const mutationObserver = setupMutationObserver(containerNode);

    // Limpeza do resizeObserver e mutationObserver quando o componente for desmontado
    return () => {
      if (resizeObserver && buttonNode) {
        resizeObserver.disconnect();
      }

      if (mutationObserver && containerNode) {
        mutationObserver.disconnect();
      }
    };
  }, [setupMutationObserver, setupResizeObserver]);

  return (
    <div className={styles.defaultPopup} ref={popupContainerRef}>
      <Popup
        trigger={<div ref={buttonRef}>{children}</div>}
        content={tag}
        position='top center'
        positionFixed={true}
        inverted
        on={'hover'}
        mountNode={popupContainerRef.current} // Monta o popup após o componente de trigger
        className={styles.popup}
        style={{
          position: 'absolute', // Define um posicionamento absoluto
          top: `${popupPosition.top}px`, // Posicionamento dinâmico
          left: `${popupPosition.left}px`, // Posicionamento dinâmico
          transform: 'none', // Desabilita o transform do Semantic UI
          willChange: 'none' // Desabilita o will-transform do Semantic UI
        }}
      />
    </div>
  );
}

DefaultPopup.defaultProps = {
  tag: 'Popup'
};

DefaultPopup.propTypes = {
  children: PropTypes.node,
  parentRef: PropTypes.node,
  tag: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

export default DefaultPopup;
